import { useState } from 'react'

import getSymbolFromCurrency from 'currency-symbol-map'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Heading,
  MenuItemProps,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'

import api from '@/api'
import { useBillingInfo } from '@/react-query/team'
import { useUser } from '@/react-query/user'

export const Billing = () => {
  const { data: user } = useUser()
  if (!user?.plan) {
    return null
  }

  const billingData = useBillingInfo()

  const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
  }

  const totalPrice =
    ((billingData.data?.price || 0) / 100) *
    (billingData.data?.subscriptionsCount || 0)

  const navigate = useNavigate()
  const [isPortalLoading, setIsPortalLoading] = useState<boolean>(false)

  const handleOpenPortal: MenuItemProps['onClick'] = async (e) => {
    try {
      e.preventDefault()

      setIsPortalLoading(true)

      const { portalSessionUrl } = (await api.createPortalSession()).data

      window.location.href = portalSessionUrl
    } catch (error) {
      console.error(error)
      setIsPortalLoading(false)
    }
  }

  const dateFormat = Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const currencySymbol = getSymbolFromCurrency(
    billingData.data?.currency?.toUpperCase() || 'USD',
  )

  return (
    <VStack spacing={10} mt={20} fontWeight={500} alignItems={'flex-start'}>
      <Heading fontSize="2xl" fontWeight="bold">
        Billing
      </Heading>
      <AnimatePresence>
        {billingData.data && (
          <Stack
            spacing={{ base: 4, md: 16 }}
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            direction={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'flex-start', md: 'center' }}
          >
            {billingData.data.plan === 'FREE' ? (
              <Text>Current plan: {capitalize(billingData.data.plan)}</Text>
            ) : (
              <>
                <Text>
                  Current plan: {capitalize(billingData.data.plan)} (
                  {billingData.data.billingPeriod?.toLowerCase()})
                </Text>
                <Text>
                  {billingData.data.subscriptionsCount} seats × {currencySymbol}
                  {(billingData.data.price || 0) / 100} = {currencySymbol}
                  {totalPrice}
                </Text>
                {billingData.data.renewalDate && (
                  <Text>
                    Renews:{' '}
                    {dateFormat.format(new Date(billingData.data.renewalDate))}
                  </Text>
                )}
              </>
            )}
            {billingData.data.plan === 'FREE' ? (
              <Button
                variant="brand-solid-gray"
                onClick={() => navigate('/pricing-page')}
                flexShrink={0}
              >
                Upgrade
              </Button>
            ) : (
              <Button
                variant="brand-solid-gray"
                isLoading={isPortalLoading}
                onClick={handleOpenPortal}
                flexShrink={0}
              >
                Manage subscription
              </Button>
            )}
          </Stack>
        )}
      </AnimatePresence>
    </VStack>
  )
}
